import { getWebSocketUrl } from "../config";
// Type definitions for analytics data
interface PageViewData {
  sessionId: string;
  path: string;
  duration?: number;
  deviceType?: string;
  browserType?: string;
  referrer?: string;
}
// Service for anonymous analytics tracking
// Compliant with GDPR - no personal data collected
class AnalyticsService {
  private sessionId: string;
  private startTime: number;
  private currentPath: string;
  private enabled: boolean;
  private lastTrackedPath: string | null = null;
  private lastTrackTime: number = 0;
  constructor() {
    // Generate ephemeral session ID (not persistent between visits)
    this.sessionId = this.getOrCreateSessionId();
    this.startTime = Date.now();
    this.currentPath = "";
    this.enabled = true;
  }
  // Get or create session ID from sessionStorage (not localStorage - ephemeral)
  private getOrCreateSessionId(): string {
    try {
      const existingId = sessionStorage.getItem("analytics_session_id");
      if (existingId) {
        return existingId;
      }
      // Generate new UUID v4
      const newId = this.generateUUID();
      sessionStorage.setItem("analytics_session_id", newId);
      return newId;
    } catch {
      // Fallback if sessionStorage is not available
      return this.generateUUID();
    }
  }
  // Generate UUID v4
  private generateUUID(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
  // Detect device type from user agent
  private getDeviceType(): string {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes("mobile")) return "mobile";
    if (ua.includes("tablet") || ua.includes("ipad")) return "tablet";
    return "desktop";
  }
  // Detect browser type (without version)
  private getBrowserType(): string {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes("chrome") && !ua.includes("edge")) return "chrome";
    if (ua.includes("firefox")) return "firefox";
    if (ua.includes("safari") && !ua.includes("chrome")) return "safari";
    if (ua.includes("edge")) return "edge";
    return "other";
  }
  // Extract domain from referrer
  private getReferrerDomain(): string | undefined {
    try {
      if (document.referrer) {
        const url = new URL(document.referrer);
        return url.hostname;
      }
    } catch {
      // Ignore errors
    }
    return undefined;
  }
  // Send data to backend with retry logic
  private async sendToBackend(data: PageViewData): Promise<void> {
    if (!this.enabled) return;
    try {
      const apiUrl = getWebSocketUrl();
      const response = await fetch(`${apiUrl}/tracking/pageview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.warn(
          "Failed to send analytics data:",
          response.status,
          response.statusText,
          errorText,
        );
      }
    } catch (error) {
      console.warn("Failed to send analytics data:", error);
      // Silently fail - analytics should not break the app
    }
  }
  // Check if path should be tracked (exclude admin pages)
  private shouldTrackPath(path: string): boolean {
    // Exclude admin pages from analytics
    return !path.startsWith("/admin");
  }
  // Track a page view
  async trackPageView(path: string): Promise<void> {
    // Skip tracking for admin pages
    if (!this.shouldTrackPath(path)) {
      this.currentPath = path;
      this.startTime = Date.now();
      return;
    }

    // Prevent duplicate tracking of the same path within 1 second
    const now = Date.now();
    if (this.lastTrackedPath === path && now - this.lastTrackTime < 1000) {
      this.currentPath = path;
      this.startTime = now;
      return;
    }

    // Calculate duration for previous page and send it (only if it's different from current)
    if (
      this.currentPath &&
      this.currentPath !== path &&
      this.shouldTrackPath(this.currentPath)
    ) {
      const duration = Math.floor((now - this.startTime) / 1000);
      await this.sendToBackend({
        sessionId: this.sessionId,
        path: this.currentPath,
        duration,
        deviceType: this.getDeviceType(),
        browserType: this.getBrowserType(),
        referrer: this.getReferrerDomain(),
      });
    }

    // Update current page tracking
    this.currentPath = path;
    this.startTime = now;
    this.lastTrackedPath = path;
    this.lastTrackTime = now;

    // Don't send page view without duration - only track when user leaves
    // This avoids creating multiple records per page
  }
  // Track page leave (before navigation or close)
  async trackPageLeave(): Promise<void> {
    if (!this.currentPath || !this.shouldTrackPath(this.currentPath)) return;
    const duration = Math.floor((Date.now() - this.startTime) / 1000);

    // Don't send if duration is too short (< 2 seconds)
    // This avoids tracking accidental clicks or immediate bounces
    // Use sendBeacon for reliable delivery on page unload
    const apiUrl = getWebSocketUrl();
    const data = {
      sessionId: this.sessionId,
      path: this.currentPath,
      duration,
      deviceType: this.getDeviceType(),
      browserType: this.getBrowserType(),
    };
    try {
      // Try sendBeacon first (more reliable for page unload)
      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(data)], {
          type: "application/json",
        });
        navigator.sendBeacon(`${apiUrl}/tracking/pageview`, blob);
      } else {
        // Fallback to regular fetch
        await this.sendToBackend(data);
      }
    } catch (error) {
      console.warn("Failed to send page leave analytics:", error);
    }
  }
  // Disable analytics tracking
  disable(): void {
    this.enabled = false;
  }
  // Enable analytics tracking
  enable(): void {
    this.enabled = true;
  }
}
// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;
